import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Hub } from "aws-amplify";
import { createFeatureAccess, FeatureAccess } from "../utils/featureAccess";
import { getWhoAmI } from "../application/rest-client";
import { BROWSER_STORE_IS_AUTHENTICATED } from "../config/constants";

const FeatureAccessContext = createContext<FeatureAccess | undefined>(undefined);

const isAuthenticated = (): boolean =>
  (window.sessionStorage.getItem(BROWSER_STORE_IS_AUTHENTICATED) || "false") === "true";

export const FeatureAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [featureAccess, setFeatureAccess] = useState<FeatureAccess | undefined>(undefined);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // force bypasses the sessionStorage gate: Amplify Hub events fire before login-presenter sets the flag
  const fetchFeatureAccess = useCallback((force = false) => {
    if (!force && !isAuthenticated()) {
      return;
    }
    const requestId = ++requestIdRef.current;
    getWhoAmI()
      .then(user => {
        // ignore responses superseded by a newer request
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        setFeatureAccess(createFeatureAccess(user));
      })
      .catch(error => {
        console.error("Failed to fetch user permissions:", error);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchFeatureAccess();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload?.event) {
        case "signIn":
        case "autoSignIn":
        case "tokenRefresh":
          fetchFeatureAccess(true);
          break;
        case "signOut":
          requestIdRef.current++;
          setFeatureAccess(undefined);
          break;
        default:
          break;
      }
    });

    return () => {
      mountedRef.current = false;
      requestIdRef.current++;
      unsubscribe();
    };
  }, [fetchFeatureAccess]);

  return (
    <FeatureAccessContext.Provider value={featureAccess}>
      {children}
    </FeatureAccessContext.Provider>
  );
};

export const useFeatureAccessContext = (): FeatureAccess | undefined =>
  useContext(FeatureAccessContext);
